/**
 * lib/loadLogo.ts
 * Loads the school logo as a data URL for embedding on the report
 * cover. Tries several candidate paths (the same set the UI
 * SchoolLogo uses) and returns the first that loads; null if none,
 * so the renderer falls back to the drawn crest. Cached per session.
 *
 * Preferred file location:  public/images/lkc_logo.png
 * (Vite serves it at /images/lkc_logo.png).
 */
let cached: string | null | undefined;

const CANDIDATES = [
  '/images/lkc_logo.png',
  '/images/lkc-logo.png',
  '/lkc_logo.png',
  '/lkc-logo.png',
];

async function tryLoad(src: string): Promise<string | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function loadLogoDataUrl(
  src?: string,
): Promise<string | null> {
  if (cached !== undefined) return cached;
  const list = src ? [src, ...CANDIDATES] : CANDIDATES;
  for (const candidate of list) {
    const data = await tryLoad(candidate);
    if (data) {
      cached = data;
      return data;
    }
  }
  cached = null;
  return null;
}
